import { Dropdown, FormField, Icon } from 'semantic-ui-react';

import { PERSON_DOCUMENT_ACCEPT, PERSON_DOCUMENT_KIND_OPTIONS } from '../../../utils/registration';
import type { PendingDocument } from '../../../hooks/usePersonDocumentUpload';

/**
 * Attachments collected alongside the registration form — a thumbprint signature, a parental
 * permission slip, a scan of the form itself.
 *
 * Queued here and uploaded **after** the person is created, because the presigned upload needs
 * a person id to hang the row off. That is the same sequence the photo already follows.
 */

export const SupportingDocuments = ({
    documents,
    onChange,
    disabled,
}: {
    documents: PendingDocument[];
    onChange: (next: PendingDocument[]) => void;
    disabled?: boolean;
}) => {
    const update = (index: number, patch: Partial<PendingDocument>) =>
        onChange(documents.map((d, i) => (i === index ? { ...d, ...patch } : d)));

    const remove = (index: number) => onChange(documents.filter((_, i) => i !== index));

    const addFiles = (files: FileList | null) => {
        if (files == null || files.length === 0) return;
        onChange([
            ...documents,
            // Defaulting the kind rather than demanding it first: the common case is a stack of
            // permission slips, and a wrong default is one dropdown to fix.
            ...Array.from(files).map((file) => ({ kind: 'PERMISSION_SLIP', file })),
        ]);
    };

    return (
        <div className="creator-section">
            <div className="creator-section-title">Supporting documents</div>
            <p className="creator-section-hint">
                For example a photo of their signature or thumbprint, the signed registration
                form, or a parental permission slip. Photographs or PDFs. Visible afterwards on
                this person's profile to admins, Centre Managers and social workers who work
                with them.
            </p>

            {documents.map((doc, index) => (
                <div className="document-row" key={`${doc.file.name}-${index}`}>
                    <Dropdown
                        selection
                        compact
                        disabled={disabled}
                        options={PERSON_DOCUMENT_KIND_OPTIONS}
                        value={doc.kind}
                        onChange={(_, { value }) => update(index, { kind: String(value) })}
                    />
                    <span className="document-name">{doc.file.name}</span>
                    <Icon
                        name="times circle"
                        color="red"
                        link
                        disabled={disabled}
                        onClick={() => !disabled && remove(index)}
                    />
                </div>
            ))}

            <FormField>
                <label htmlFor="person-document-input">Add a document</label>
                <input
                    id="person-document-input"
                    type="file"
                    multiple
                    disabled={disabled}
                    accept={PERSON_DOCUMENT_ACCEPT}
                    onChange={(e) => {
                        addFiles(e.target.files);
                        // Clear the input so re-picking the same file fires `change` again.
                        e.target.value = '';
                    }}
                />
            </FormField>
        </div>
    );
};
